import React, { useState } from "react";
import { NavLink } from "react-router-dom";

// Importing bootstrap components
import { Col, Row, Card, Button, Spinner } from "react-bootstrap";

// Importing components
import EmployeeForm from "components/EmployeeForm";

// Importing toast utilities
import { showSuccessToast } from "utils/toast";

// Importing redux components
import { useSelector, useDispatch } from "react-redux";

// Importing auth slice
import { updateUserInfo } from "state/slices/auth/authSlice";

// Importing user slice
import { updateEmployee as updateEmployeeInStore } from "state/slices/user/userSlice";

// Importing user mutations
import {
  useDeleteUserProfilePictureMutation,
} from "state/slices/user/userApiSlice";

import { useUpdateProfileMutation } from "state/slices/profile/profileApiSlice";

// Importing other components slice
import { useGetDesignationsQuery } from "state/slices/designation/designationApiSlice";
import { useGetTeamsQuery } from "state/slices/team/teamApiSlice";
import { useGetShiftsQuery } from "state/slices/shift/shiftApiSlice";
import { useGetRolesQuery } from "state/slices/role/roleApiSlice";

// TODO: CREATE DIFFERENT COMPONENT FOR VIEW PROFILE AND USE IT

// Importing validators
import {
  valiDateOptionalFormField,
  isNull,
  isFile,
  parseUserData,
} from "validations";
import { parseDate } from "utils/date";

// Import utilities
import { isAllowedToDoByKey } from 'permissions-hub';
import { EMPLOYEE_MANAGEMENT, ACCEPTED_PERMISSIONS } from "constants";
import { getAllowedPermissions } from "utils/authentication";

const GeneralAccessManagement = React.memo(({
  isAllowedToDelete,
}) => {
  const dispatch = useDispatch();
  const { userInfo: loggedUserInfo } = useSelector((state) => state.auth);

  const isAllowedToAccessOtherUsers = isAllowedToDoByKey(
    EMPLOYEE_MANAGEMENT,
    ACCEPTED_PERMISSIONS["update"],
    getAllowedPermissions(loggedUserInfo),
    false,
  );

  // Retrieve Designations
  const { data: { designations: designationOptions } = {} } = useGetDesignationsQuery();
  // const designationOptions = useSelector((state) => state.designation.designations);

  // Retrieve teams
  const { data: { teams: teamOptions } = {} } = useGetTeamsQuery();
  // const teamOptions = useSelector((state) => state.team.teams);

  // Retrieve shifts
  const { data: { shifts: shiftOptions } = {} } = useGetShiftsQuery();
  // const shiftOptions = useSelector((state) => state.shift.shifts);

  // Retrieve roles
  const { data: { roles: roleOptions } = {} } = useGetRolesQuery();
  // const roleOptions = useSelector((state) => state.role.roles);

  const [initialValues, setInitialValues] = useState(() => {
    let formatedUserData = null;

    if (!isNull(loggedUserInfo)) {
      formatedUserData = parseUserData(loggedUserInfo);
    }

    return formatedUserData;
  });

  const [updateProfile, { isLoading: isUpdatingProfile }] = useUpdateProfileMutation();
  const [deleteUserProfilePicture] = useDeleteUserProfilePictureMutation();

  const handleSubmit = async (values) => {
    try {
      
      const formData = new FormData();

      const fieldsToCheck = [
        "name",
        "phone",
        "email",
        "employeeId",
        "designation",
        "team",
        "shift",
        "role",
        "dateOfJoining",
        "dateOfBirth",
        "address",
        "cl",
        "totalDD",
        "leaves",
        "password",
        "profilePicture",
      ];

      fieldsToCheck.forEach((field) => {
        // Check if the field exists in both loggedUserInfo and values
        if (!isNull(loggedUserInfo)) {
          if (
            loggedUserInfo.hasOwnProperty(field) &&
            values.hasOwnProperty(field)
          ) {
            // Check if the values are different
            let comparedTo = loggedUserInfo[field];
            if (
              ["designation", "shift", "team", "role"].includes(field)
            ) {
              comparedTo = loggedUserInfo[field].id;
            }

            if (
              ["dateOfJoining", "dateOfBirth"].includes(field)
            ) {
              // Dates are now in ISO format from server, parse to yyyy-MM-dd for comparison
              comparedTo = parseDate("yyyy-MM-dd", loggedUserInfo[field]);
            }

            if (comparedTo !== values[field]) {
              // If different, append the field to the formData
              formData.append(field, values[field]);
            }
          } else {
            // If the field is not present in loggedUserInfo, append the field from values
            if (
              values.hasOwnProperty(field) &&
              valiDateOptionalFormField(values[field])
            ) {
              formData.append(field, values[field]);
            }
          }
        } else {
          // If the field is not present in loggedUserInfo, append the field from values
          if (
            values.hasOwnProperty(field) &&
            valiDateOptionalFormField(values[field])
          ) {
            formData.append(field, values[field]);
          }
        }
      });

      // Handle comparison bank details
      if (
        valiDateOptionalFormField(values.bankName) &&
        values.bankName !== loggedUserInfo?.bankDetails?.bankName
      ) {
        formData.append("bankDetails[bankName]", values.bankName);
      }

      if (
        valiDateOptionalFormField(values.accountNumber) &&
        values.accountNumber !== loggedUserInfo?.bankDetails?.accountNumber
      ) {
        formData.append("bankDetails[accountNumber]", values.accountNumber);
      }

      if (
        valiDateOptionalFormField(values.ifscCode) &&
        values.ifscCode !== loggedUserInfo?.bankDetails?.ifscCode
      ) {
        formData.append("bankDetails[ifscCode]", values.ifscCode);
      }

      // Handle comparison biometric details
      if (
        isFile(values.profilePicture) &&
        values.profilePicture !== loggedUserInfo.biometricDetails?.profilePicture
      ) {
        formData.append(
          'profilePicture',
          values.profilePicture
        );
      }

      // Use the "updateProfile" mutation function for updating an existing user
      const updatedUser = await updateProfile(formData).unwrap();

      const formatUpdatedUserData = parseUserData(updatedUser);

      // Update user in local store
      dispatch(updateEmployeeInStore(formatUpdatedUserData));

      // Update user in local storage
      dispatch(
        updateUserInfo({
          field: "replace",
          data: updatedUser,
        })
      );

      showSuccessToast("Profile updated successfully");
    } catch (err) {
      // Error toast is automatically shown by baseQuery
    }
  };

  return (
    <section className="section dashboard">
      <Row>
        <Col lg={12}>
          <Card className="p-3">
            <Card.Body>
              <EmployeeForm
                formId="update_profile_form"
                handleSubmit={handleSubmit}
                designationOptions={designationOptions}
                teamOptions={teamOptions}
                shiftOptions={shiftOptions}
                roleOptions={roleOptions}
                initialValues={initialValues}
                isAllowedToUpdate={isAllowedToAccessOtherUsers}
                isAllowedToDelete={isAllowedToDelete}
                deleteUserProfilePicture={deleteUserProfilePicture}
              />
            </Card.Body>
            <Card.Footer>
              <Button
                variant="primary"
                form="update_profile_form"
                type="submit"
                className="me-3"
                disabled={isUpdatingProfile}
              >
                {isUpdatingProfile ? (
                  <>
                    <Spinner
                      animation="border"
                      size="sm"
                      role="status"
                      className="me-2"
                      style={{ width: "18px", height: "18px" }}
                    />
                    Updating...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
              <Button variant="secondary" as={NavLink} to="/" disabled={isUpdatingProfile}>
                Cancel
              </Button>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </section>
  );
});

export default GeneralAccessManagement;
